<template>
  <div id="editPage">
    <email-frequency @refreshBookmark="getBookmarks" :selectedDashboard="selectedDashboard"/>
    <div class="flex justify-between space-x-4">
      <PackageList v-if="showCaseCreation" class="mb-2" />
      <div class="flex justify-end space-x-4 ml-auto">
        <Button
        :text="$t('general.edit_dashboard')"
        type="primary"
        @click="loadDesigner(false)"
        v-if="!designerView && showDashboardDesigner"
        />
        <Button
        :text="$t('general.create_dashboard')"
        type="primary"
        @click="loadDesigner(true)"
        v-if="!designerView && showDashboardDesigner"
        />
        <dropdown 
          v-if="!designerView"
          @handleIconClick="handleClick"
          v-model="selectedView"
          @input="loadView()" 
          :placeholder="$t('pages.bi_dashboard.saved_filters')"
          :options="bookmarksList" 
          :config="{ label: 'name', trackBy: 'view_id' }" 
          class="w-full" 
          width="392px"
          minWidth="182px" 
          :colors="{
            text: '#282828',
            bg: 'white',
            svg: '#282828',
            border: '#e8e8e8',
          }" 
          style="z-index: 501">
        </dropdown>
        <dropdown v-if="!designerView" reposition searchable class="filter-dropdown h-10" :placeholder="$t('pages.tenant_client.dashboards')"
          :options="dashboardOptions" v-model="selectedDashboard" :colors="{
            text: '#282828',
            bg: 'white',
            svg: '#282828',
            border: '#e8e8e8',
          }" :limit="1" width="192px" minWidth="102px" :config="{ label: 'labelName', trackBy: 'dashboard_id' }"
          @input="selectDashboard" style="z-index: 501" />
      </div>
    </div>
    <button v-if="designerView" class="my-3 px-5 py-2 text-sm text-white border-0 outline-none ml-2 
    bg-blue-600 hover:bg-brand-hover font-medium rounded-lg cursor-pointer  drop-shadow-sm" @click="backDashboard">
    Back
    </button>
    <div id="dashboard_container" class="mt-3" ></div>
    <CaseAccessControlModal
      ref="case-access-control-modal"
      :is-opened="showCaseAccessControlModal"
      @hidden="onCaseAccessControlClose"
      @onSubmit="onCaseAccessControlSubmit"
      @onLoadStart="loading = true"
      @onLoadEnd="loading = false"
      :isCaseDashboard = "isCaseDashboard"
      :selectedCaseIds = "selectedCaseIds"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { BoldBI } from "@boldbi/boldbi-embedded-sdk";
import BoldBiConfig from "@/boldbi/config";
import biHelper from "@/boldbi";
import VueCookies from "vue-cookies";
import dropdown from "@shared/components/dropdown-base";
import axios from "@/axios"
import emailFrequency from "./components/emailFrequency.vue"
import { EventBus } from "@/main.js";
import { PackageList } from "@shared/case-creation";
import { checkPermission } from "@shared/utils/functions";
import { userType, DashboardLanguageCode } from "@shared/utils/constants.js";
import dashboardService from "./services.js";
const Button = () => import("@/components/button");
const CaseAccessControlModal = () => import("@shared/case-entities/containers/CaseAccessControlModal.vue");

export default {
  name: "Bi-Dashboard-View",

  data() {
    return {
      dashboardOptions: [],
      selectedDashboard: [],
      bookmarksList: [],
      selectedView: null,
      refresh: 0,
      boldbiEmbedInstance: null,
      showCaseAccessControlModal: false,
      loading: false,
      gridSelectedRows: [],
      selectedCaseIds: [],
      isCaseDashboard: false,
      designerView: false,
    };
  },

  components: {
    Button,
    dropdown,
    emailFrequency,
    PackageList,
    CaseAccessControlModal,
  },

  computed: {
    ...mapGetters({
      getIsClient: "getIsClient",
      getUserType: "getUserType",
      currentLangProfile: "LocalizationStore/getCurrentLangProfile",
    }),

    showCaseCreation() {
      return this.getIsClient && checkPermission("case.read");
    },
    showDashboardDesigner() {
      return checkPermission("dashboard.designer")
    },
    caseAllocationPermission() {
      return (checkPermission('case.allocate') && checkPermission('users.read') && checkPermission('groups.read') && checkPermission("case.assignment_update") && checkPermission("case.edit"));
    },

    caseAssignmentUpdatePermission() {
      return checkPermission("case.assignment_update") && checkPermission("case.case_allocation");
    },

    viewId() {
      if (this?.$route?.query?.view_id) {
        return this.$route.query.view_id;
      } else {
        return null;
      }
    },
    dashboardId() {
      return this.$route.params.id;
    },
    isClient() {
      return this.getUserType == userType.CLIENT
    },
  },

  async mounted() {
    await this.getTenantDashboards();
    await this.getBookmarks();
    await this.initLang();

    this.reloadBoldBIDashboard();
  },

  beforeDestroy() {
    this.resetMultipleCaseAccessData();
    this.cleanupDashboard();
  },

  methods: {
    ...mapActions({
      fetchUserLangPreference: "LocalizationStore/fetchUserLangPreference",
      fetchMultipleCaseAccessData: "CaseEntitiesStore/fetchMultipleCaseAccessData",
      fetchClientUsers: "CaseEntitiesStore/fetchCaseNotesUsers",
      resetMultipleCaseAccessData: "CaseEntitiesStore/resetMultipleCaseAccessData",
    }),
    async backDashboard() {
      await this.getTenantDashboards();
      await this.getBookmarks();
      await this.initLang();
      this.designerView = false
      this.reloadBoldBIDashboard();
    },
    loadDesigner(newDashboard) {
      this.designerView = true
      let token = VueCookies.get("tenant_token") || VueCookies.get("client_token");
      const boldBiHeader = {
        Authorization: "Bearer " + token,
      };
      let boldbiData = {
        serverUrl: BoldBiConfig.BASE_URL,
        embedContainerId: "dashboard_container", // This should be the container id where you want to embed the dashboard
        mode: BoldBI.Mode.Design,
        height: "calc(100% - 48px)",
        width: "100%",
        authorizationServer: {
          url: biHelper.generateEmbedUrl(),
          headers: boldBiHeader,
        },
        actionComplete: async function(args) {
          if (args.eventType == 'SaveAs' && newDashboard) {
            const payload = {
              dashboard_id: args.data.dashboardId,
              dashboard_name: args.data.dashboardName,
              assign_tenant: true
            }
            await dashboardService.publishDashboard(payload)
            this.$toast.success("Dashboard Published Successfully.")
          }
        },
        onError: function () {
          document.getElementById("dashboard_container").style.textAlign = "center"
          // document.getElementById("dashboard_container").style.marginTop = "20%"
          document.getElementById("dashboard_container").style.color = "red"
          document.getElementById("dashboard_container").innerHTML = "<h3>Error in loading dashboard, Please Refresh the page.</h3>"
        }  
      }

      if (!newDashboard) {
        const dashboardId = this.$route.params.id
        boldbiData.dashboardId = dashboardId
      }
      this.boldbiEmbedInstance = BoldBI.create(boldbiData);
      this.boldbiEmbedInstance.loadDesigner(); 
    },
    loadView() {
      this.loadDashboardBi(this.selectedDashboard.dashboard_id, true)
    },
    handleClick(icon, freq) {
      if (icon == 'EditIcon') {
        EventBus.$emit("EditDashboardView", freq.option);
      }
      else if (icon == 'DeleteIcon') {
        EventBus.$emit("DeleteDashboardView", freq.option);
      }
      else {
        EventBus.$emit("showEmailFreq", freq.option);
      }
    },
    handleSaveModal() {
      this.$modal.show("saveFilter");
    },
    async getBookmarks() {
      try {
        const { data } = await axios.get(`/dashboard/bookmarks/${this.selectedDashboard.tenant_dashboard_id || this.selectedDashboard.client_dashboard_id}`);
        this.bookmarksList = data.views;
        this.bookmarksList.forEach((res) => {
          res["icons"] = []
          res.icons.push("EditIcon")
          if (res.send_email) {
            res.icons.push("EmailGreen")
          }
          else {
            res.icons.push("EmailDefault")
          }
          res.icons.push("DeleteIcon")

        })

      } catch {
        return {};
      }
    },

    async initLang() {
        if (!checkPermission("language.language_dropdown")) return;
        try {
          await this.fetchUserLangPreference();
        } catch (error) {
            console.error("Failed to load language preferences", error);
        }
    },

    reloadBoldBIDashboard() {
      const dashboardId = this.$route.params.id;
      this.loadDashboardBi(dashboardId);
    },

    loadDashboardBi(dashboardId, loadView=false) {
      this.cleanupDashboard()
      let token = VueCookies.get("tenant_token") || VueCookies.get("client_token");
      const boldBiHeader = {
        Authorization: "Bearer " + token,
      };
      var gridSelectedRows = [];
      var that = this;

      let boldbiData = {
        serverUrl: BoldBiConfig.BASE_URL,
        dashboardId: dashboardId,
        embedContainerId: "dashboard_container", // This should be the container id where you want to embed the dashboard
        mode: BoldBI.Mode.View,
        height: "calc(100% - 48px)",
        width: "100%",
        authorizationServer: {
          url: biHelper.generateEmbedUrl(),
          headers: boldBiHeader,
        },
        expirationTime: "100000",
        dashboardSettings: {
          beforeSaveViewDialogOpen: function (args) {
            args.cancel = true;
            const payload = {
              filters: args.data.encryptedData,
              dashboard_id: this.dashboardId,
              name: "",
            };
            EventBus.$emit("saveDashboardView", payload);
          },
          filterOverviewSettings: {
            showSaveIcon: true,
          }
        },
        widgetSettings: {
            beforeWidgetControlMenuOpen: function (args) {
            // Add Custom Icon into the grid widget
                if(args.widgetType.toLowerCase() === "grid") { 
                    args.menuItems.push({
                        "id":"selectedrowingrid",
                        "parentId": null,
                        "sprite":"bbi-dashboard-selectedrow-menu",
                        "text": "Case Allocation"
                    });
                }
            },
            onWidgetControlMenuClick: function (args) {
              if (args.menuItem.ID === "selectedrowingrid" && !that.caseAssignmentUpdatePermission) {
                return that.$toast.error(that.$t("errors.permissions.case.assignment_update"));
              }

              if (gridSelectedRows.length) {
                that.selectedCaseIds = [...new Set(gridSelectedRows.map(row => row['Case Id']).filter(id => id))];
                that.$forceUpdate();
                that.showCaseAccessControlModal = true;
                that.isCaseDashboard = true;
                that.$refs["case-access-control-modal"]?.$refs["case-access-control-content"]?.showModal();
              }
            }
        },
        beforeFilterApply: function(args) {
              // While applying the filter, get the selected row information with column name and stored it in the variable gridSelectedRows.
              if(args.eventType === "filterInteraction" && args.source.data.widgetProperties.UniqueName.toLowerCase().includes("grid")) {
                gridSelectedRows = []
                const container = args.source.data.widgetProperties.Data.Containers[0];
                const columnNames = container.FieldInfos.map(info => info.DisplayName);
                const uniqueColumnNames = container.FieldInfos.map(info => info.UniqueColumnName);
                const selectedRows = args.source.data.selectedRow.data;
                const multiSelectEnabled = args.source.data.widgetProperties.ShouldEnableCheckBox;

                if (multiSelectEnabled) {
                    selectedRows.forEach(row => {
                        const modifiedRow = {};
                        uniqueColumnNames.forEach((key, index) => {
                            modifiedRow[columnNames[index]] = row[key];
                    });
                    gridSelectedRows.push(modifiedRow);
                  });
                } else {
                    const modifiedRow = {};
                    uniqueColumnNames.forEach((key, index) => {
                        modifiedRow[columnNames[index]] = selectedRows[index];
                    });
                    gridSelectedRows.push(modifiedRow);
                }
            }
        },
        // actionComplete: function(args) {
        //     if(args.eventType === "clearWidgetFilter" && args.source.data.widgetProperties.UniqueName.toLowerCase().includes("grid")) {
        //       gridSelectedRows = [];
        //     }
        // },

        onError: function () {
          document.getElementById("dashboard_container").style.textAlign = "center"
          // document.getElementById("dashboard_container").style.marginTop = "20%"
          document.getElementById("dashboard_container").style.color = "red"
          document.getElementById("dashboard_container").innerHTML = "<h3>Error in loading dashboard, Please Refresh the page.</h3>"
        }  
      };
      if (DashboardLanguageCode[this.currentLangProfile.iso_code]) {
        boldbiData.languageSettings = {
          hideLanguageDropdown: true,
          languageCode: DashboardLanguageCode[this.currentLangProfile.iso_code]
        }
      }
      if (this.viewId || loadView) {
        if(!loadView) {
        boldbiData.dashboardSettings.filterOverviewSettings = {
          viewId: this.$route.query.view_id,
          viewName: this.$route.query.view_name,
          showSaveIcon: true,
        };
        boldbiData.filterParameters = this.$route.query.filters;
        }
        else {
          boldbiData.dashboardSettings.filterOverviewSettings = {
          viewId: this.selectedView.view_id,
          viewName: this.selectedView.name,
          showSaveIcon: true,
        }; 
        boldbiData.filterParameters = this.selectedView.filters;
        }
      }
      this.boldbiEmbedInstance = BoldBI.create(boldbiData);
      setTimeout(()=>{
        this.boldbiEmbedInstance.loadDashboard();
      },1000)
    },

    toggleItems() {
      if (this.gridSelectedRows.length >= 10) {
        this.gridSelectedRows = []
      }
    },

    cleanupDashboard() {
      if (this.boldbiEmbedInstance) {
        try {
          this.boldbiEmbedInstance.destroy();
        } catch (error) {
          console.warn('Error cleaning up dashboard:', error);
        }
        this.boldbiEmbedInstance = null;
      }

      const container = document.getElementById('dashboard_container');
      if (container) {
        container.innerHTML = '';
      }
    },

    async selectDashboard() {
      await this.getBookmarks()
      if (this.selectedDashboard?.name != 'Default Dashboard') {
        this.$router.replace({
          name: "Bi-Dashboard-View",
          params: {
            id: this.selectedDashboard.dashboard_id,
          },
        });
        this.loadDashboardBi(this.selectedDashboard.dashboard_id);
      } else {
        this.$router.push({
          path: "default-dashboard",
          name: "Dashboard - Page 2",
          params: {
            id: process.env.VUE_APP_DEFAULT_DASHBOARD_KEY,
          },
        });

      }
    },
    async getTenantDashboards() {
      try {
        const { data } = await axios.get("/dashboard/assigned-dashboards");
        this.dashboardOptions = data.dashboards
          .map((dashboard) => {
            let bbDashboard = dashboard;
            if (dashboard.name.includes("BB_")) {
              bbDashboard.labelName = dashboard.name.split("_")[1];
            } else {
              bbDashboard.labelName = dashboard.name;
            }

            bbDashboard.name = dashboard.name;
            return bbDashboard;
          });
        if (this.$route.params.id) {
          this.selectedDashboard = this.dashboardOptions.find((res) => res.dashboard_id == this.$route.params.id)
        }
        else {
          this.selectedDashboard = this.dashboardOptions?.[0];
        }
        if (this.selectedDashboard?.name != 'Default Dashboard') {
          this.$router.push({
            name: "Bi-Dashboard-View",
            params: {
              id: this.selectedDashboard.dashboard_id,
            },
            query: { ...this.$route.query }
          });
        }
        else {
          this.$router.push({
            name: "default-dashboard"
          });
        }
      } catch (err) {
        console.log(err);
      }
    },
    routeBack() {
      this.$router.push({ name: "Dashboard - Page 2" });
    },

    onCaseAccessControlClose() {
      this.showCaseAccessControlModal = false;
    },
    
    async onCaseAccessControlSubmit() {
      this.$refs["case-access-control-modal"]?.$refs["case-access-control-content"]?.hideModal();
      this.onCaseAccessControlClose();
      // await this.fetchClientUsers({ clientId: this.getCaseData.client_id, caseId:this.case_id });
    },
  },
};
</script>

<style scoped lang="scss">
#editPage {
  width: 100%;
  height: 100%;
}
</style>
